import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthChrome } from '../components/auth/AuthChrome';
import {
  Banner,
  Button,
  ButtonRouterLink,
  Card,
  PasswordField,
  PasswordStrengthMeter,
  Spinner,
  TextField
} from '../components/primitives';
import { OrgPickerList, type OrgPickerMembership } from '../components/auth/OrgPickerList';
import { supabase } from '../lib/supabase';
import {
  PASSWORD_MIN_LENGTH,
  PASSWORD_MIN_SCORE,
  scorePassword
} from '../lib/passwordScore';
import type { EmailOtpType } from '@supabase/supabase-js';
import './PasswordReset.css';

type View = 'resolving' | 'form' | 'unavailable' | 'picker';

const RESOLVING_MIN_MS = 250;
const DASHBOARD_PATH = '/dashboard';

const PASSWORD_HELP = `Minimum ${PASSWORD_MIN_LENGTH} characters. Submit unlocks at "Fair" or stronger.`;

/**
 * /password-reset/confirm?token_hash=…&type=recovery — the email-link landing.
 *
 * State machine:
 *  - resolving  — verifying the token with Supabase.
 *  - form       — token good, render the new-password form.
 *  - unavailable — token expired / used / missing / invalid. Render the
 *                  invalid-link card with "Request a new link".
 *  - picker     — submit succeeded, user has 2+ memberships → inline org-picker.
 *
 * Supabase's recovery email lands here with `code` (PKCE) or `token_hash` + `type=recovery`.
 * We accept both, plus the spec's friendly `?token=` alias.
 */
export function PasswordResetConfirm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const tokenHash =
    searchParams.get('token_hash') ?? searchParams.get('token');
  const code = searchParams.get('code');
  const type: EmailOtpType =
    (searchParams.get('type') as EmailOtpType | null) ?? 'recovery';

  const [view, setView] = useState<View>('resolving');
  const [password, setPassword] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<ReactNode>(null);

  const [memberships, setMemberships] = useState<OrgPickerMembership[]>([]);
  const [pickerBusy, setPickerBusy] = useState(false);

  const submittedRef = useRef(false);

  const score = useMemo(() => scorePassword(password), [password]);
  const longEnough = password.length >= PASSWORD_MIN_LENGTH;
  const strongEnough = longEnough && score >= PASSWORD_MIN_SCORE;
  const mismatch = !!confirmPwd && confirmPwd !== password;
  const canSubmit =
    !submitting && strongEnough && !!confirmPwd && !mismatch;

  // Verify the recovery token (or exchange the PKCE code) to establish a
  // session. On failure → unavailable card.
  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();

    async function resolve() {
      try {
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) throw error;
        } else if (tokenHash) {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type
          });
          if (error) throw error;
        } else {
          throw new Error('missing token');
        }
        await waitForResolvingFloor(startedAt);
        if (cancelled) return;
        setView('form');
      } catch {
        await waitForResolvingFloor(startedAt);
        if (cancelled) return;
        setView('unavailable');
      }
    }

    void resolve();
    return () => {
      cancelled = true;
    };
  }, [tokenHash, code, type]);

  async function fetchMembershipsForUser(userId: string): Promise<OrgPickerMembership[]> {
    // Pull all memberships for the user. Join org + function_role names so the
    // picker can render with no follow-up requests. RLS gates this to the
    // signed-in user.
    const { data, error } = await supabase
      .from('memberships')
      .select(
        'org_id, orgs:org_id(name), function_role:function_role_id(label)'
      )
      .eq('user_id', userId);
    if (error || !data) return [];
    return data.map((row) => {
      const orgs = row.orgs as { name?: string } | { name?: string }[] | null;
      const orgName =
        Array.isArray(orgs) ? orgs[0]?.name ?? '' : orgs?.name ?? '';
      const fn = row.function_role as
        | { label?: string }
        | { label?: string }[]
        | null;
      const fnName = Array.isArray(fn) ? fn[0]?.label ?? null : fn?.label ?? null;
      return {
        org_id: row.org_id,
        org_name: orgName || 'Unnamed org',
        function_role_name: fnName
      };
    });
  }

  function setActiveOrgAndGo(orgId: string) {
    // Hand the picked org id to the shell via sessionStorage. The shell
    // consumes + clears this on its next mount; otherwise it would default
    // to the user's oldest membership.
    try {
      window.sessionStorage.setItem('pn:active-org-hint', orgId);
    } catch {
      /* sessionStorage unavailable — shell falls back to oldest membership */
    }
    navigate(DASHBOARD_PATH, { replace: true });
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    if (submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    setServerError(null);

    try {
      const { data: updateData, error: updateErr } = await supabase.auth.updateUser({
        password
      });
      if (updateErr || !updateData.user) {
        submittedRef.current = false;
        setSubmitting(false);
        setServerError("Couldn't update your password. Try again.");
        return;
      }

      const userId = updateData.user.id;
      const list = await fetchMembershipsForUser(userId);

      if (list.length <= 1) {
        if (list.length === 1) {
          await setActiveOrgAndGo(list[0].org_id);
        } else {
          // No memberships visible (rare — e.g. an account with no org row yet).
          // Still navigate to /dashboard; the dashboard's no-active-org branch
          // will decide what to do.
          navigate(DASHBOARD_PATH, { replace: true });
        }
        return;
      }

      setMemberships(list);
      setView('picker');
      setSubmitting(false);
    } catch {
      submittedRef.current = false;
      setSubmitting(false);
      setServerError(
        "We couldn't reach Penrose. Check your connection and try again."
      );
    }
  }

  async function handlePick(m: OrgPickerMembership) {
    if (pickerBusy) return;
    setPickerBusy(true);
    await setActiveOrgAndGo(m.org_id);
  }

  return (
    <>
      <AuthChrome pageLabel="Reset password" />
      <main className="pr-main">
        <Card className="pr-card pr-card-wide" aria-live="polite" data-prc-view={view}>
          {view === 'resolving' ? (
            <div className="pr-resolving">
              <Spinner size="sm" label="Verifying your reset link" />
              <span className="pr-resolving-caption">
                Verifying your reset link…
              </span>
            </div>
          ) : null}

          {view === 'unavailable' ? (
            <>
              <h1 className="pr-heading">Reset link unavailable</h1>
              <p className="pr-lede">
                This reset link is no longer valid. It may have expired or
                already been used.
              </p>
              <div className="pr-actions">
                <ButtonRouterLink to="/password-reset" variant="primary" size="md">
                  Request a new link
                </ButtonRouterLink>
              </div>
            </>
          ) : null}

          {view === 'form' ? (
            <>
              <h1 className="pr-heading">Set a new password</h1>
              <p className="pr-lede">
                Choose a new password. You'll be signed in here, and signed out
                everywhere else.
              </p>

              {serverError ? <Banner kind="error">{serverError}</Banner> : null}

              <form className="pr-form" onSubmit={handleSubmit} noValidate>
                <PasswordField
                  id="prc-password"
                  label="New password"
                  autoComplete="new-password"
                  required
                  autoFocus
                  value={password}
                  onChange={(e) => setPassword(e.currentTarget.value)}
                  helpText={PASSWORD_HELP}
                  trailing={
                    <PasswordStrengthMeter
                      score={score}
                      hasInput={password.length > 0}
                    />
                  }
                />

                <TextField
                  id="prc-password-confirm"
                  label="Confirm new password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPwd}
                  onChange={(e) => setConfirmPwd(e.currentTarget.value)}
                  errorText={mismatch ? "Passwords don't match." : undefined}
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={!canSubmit}
                  className="pr-submit"
                >
                  {submitting ? 'Updating…' : 'Set new password'}
                </Button>
              </form>
            </>
          ) : null}

          {view === 'picker' ? (
            <>
              <h1 className="pr-heading">Password updated</h1>
              <p className="pr-lede">
                You belong to multiple orgs. Choose where to land:
              </p>
              <OrgPickerList
                memberships={memberships}
                onPick={handlePick}
                disabled={pickerBusy}
              />
            </>
          ) : null}
        </Card>
      </main>
    </>
  );
}

async function waitForResolvingFloor(startedAt: number) {
  const elapsed = Date.now() - startedAt;
  if (elapsed >= RESOLVING_MIN_MS) return;
  await new Promise((r) => window.setTimeout(r, RESOLVING_MIN_MS - elapsed));
}
